import React from "react";
import {
  GetCloudName,
  GetProviderName,
  GPUInstanceType,
} from "types/InstanceTypes";
import { useFreeTier, getStoragePricing, withServiceCharge, isFreeTierInstance, getBasePriceAmount } from "utils/freeTierUtils";
import { PricingRateRows } from "components/FreeTier";
import InputField from "components/UI-lib/InputField";
import { Tooltip } from "@mui/material";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import {
  ArrowPathIcon,
  BeakerIcon,
  CircleStackIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  StopCircleIcon,
} from "@heroicons/react/24/outline";
import { isStoppableInstance } from "../utils/index";
import CopyButton from "components/Graphs/CopyButton";
import RackCard from "./RackCard/index";
import CpuInstanceCard from "./CpuInstanceCard";
import cx from "classnames";
import {
  isBrevCloud,
  getStorageBoundsGiB,
  isStorageValueValid,
} from "utils/instanceTypes";
import { getPrimaryInstanceCloudCred } from "utils/cloudCreds";

interface SelectedInstanceDetailsProps {
  selectedInstance: GPUInstanceType;
  setSelectedInstance: (instanceObj: GPUInstanceType | undefined) => void;
  storage: string;
  setStorage: (size: string) => void;
  flexiblePortControlOnly?: boolean;
  flexibleStorageOnly?: boolean;
  locationName?: string | null;
}

interface StorageSectionProps {
  selectedInstance: GPUInstanceType;
  storage: string;
  setStorage: (size: string) => void;
}

const StorageSection: React.FC<StorageSectionProps> = ({
  selectedInstance,
  storage,
  setStorage,
}) => {
  if (selectedInstance.elastic_root_volume) {
    const { min: minSizeGiB, max: maxSizeGiB } =
      getStorageBoundsGiB(selectedInstance);
    const minLabel = minSizeGiB !== null ? Math.ceil(minSizeGiB) : null;
    const maxLabel = maxSizeGiB !== null ? Math.floor(maxSizeGiB) : null;
    const storageErrorMessage = isStorageValueValid(storage, selectedInstance)
      ? ""
      : minLabel !== null && maxLabel !== null
        ? `Size should be between ${minLabel}GiB and ${maxLabel}GiB`
        : minLabel !== null
          ? `Size should be at least ${minLabel}GiB`
          : maxLabel !== null
            ? `Size should be at most ${maxLabel}GiB`
            : "";

    return (
      <InputField
        label="Disk Storage (GiB)"
        placeholder="leave blank for default (256GiB)"
        value={storage}
        onChange={(e) => setStorage(e)}
        errorMessage={storageErrorMessage}
        onKeyDown={(e) => {
          if (
            !/[0-9\b]/.test(e.key) &&
            e.key !== "Backspace" &&
            e.key !== "ArrowLeft" &&
            e.key !== "ArrowRight"
          ) {
            e.preventDefault();
          }
        }}
      />
    );
  }

  const fixedSize =
    selectedInstance.supported_storage?.length > 0 &&
    selectedInstance.supported_storage[0].size
      ? selectedInstance.supported_storage[0].size
      : "Select Instance First";

  return (
    <>
      <Tooltip
        title="Disk Storage is fixed by the Cloud Provider."
        placement="top-start"
      >
        <div>
          <InputField
            label="Disk Storage (GiB)"
            placeholder="leave blank for default (256GiB)"
            value={`${fixedSize}`}
            onChange={() => {}}
            disabled
            errorMessage=""
            onKeyDown={(e) => {
              if (
                !/[0-9\b]/.test(e.key) &&
                e.key !== "Backspace" &&
                e.key !== "ArrowLeft" &&
                e.key !== "ArrowRight"
              ) {
                e.preventDefault();
              }
            }}
          />
        </div>
      </Tooltip>
      <p className="text-xs text-secondary mt-1">
        <span className="text-red-500 font-bold">*</span>Disk Storage is fixed
        by the Cloud Provider.
      </p>
    </>
  );
};

interface InstanceAttributesListProps {
  selectedInstance: GPUInstanceType;
  stoppable: boolean;
}

const InstanceAttributesList: React.FC<InstanceAttributesListProps> = ({
  selectedInstance,
  stoppable,
}) => (
  <div className="flex flex-col text-gray-700 dark:text-secondary mt-1">
    <div className="flex flex-row items-center mb-1">
      <span className="text-sm font-medium mr-2">Instance Type:</span>
      <CopyButton
        value={selectedInstance.type}
        displayValue={selectedInstance.type}
        className="text-sm"
      />
    </div>
    {getPrimaryInstanceCloudCred(selectedInstance)?.platformType ===
      "akash" && (
      <>
        <div className="flex flex-row mb-1">
          <BeakerIcon className="h-4 w-4" />
          <span className="ml-1 text-sm">
            Akash cloud is a decentralized compute provider, so you may
            encounter unexpected behaviors
          </span>
        </div>
        <div className="flex flex-row mb-1">
          <CircleStackIcon className="h-4 w-4" />
          <span className="ml-1 text-sm">
            Disk may be reset if you exceed the disk or memory limits
          </span>
        </div>
      </>
    )}
    {(selectedInstance.provider === "crusoe" ||
      selectedInstance.provider === "lambda-labs" ||
      selectedInstance.provider === "fluidstack" ||
      selectedInstance.provider === "shadeform") && (
      <div className="flex flex-row items-center">
        <ExclamationTriangleIcon className="h-4 w-4" />
        <span className="ml-1 text-sm">
          This is a pre-release option,{" "}
          <TooltipPrimitives.Provider>
            <TooltipPrimitives.Root delayDuration={300}>
              <TooltipPrimitives.Trigger asChild>
                <span className="text-highlight cursor-pointer">
                  view details
                </span>
              </TooltipPrimitives.Trigger>
              <TooltipPrimitives.Portal>
                <TooltipPrimitives.Content
                  side="top"
                  sideOffset={4}
                  className="z-50 max-w-xs rounded-md bg-zinc-800 px-3 py-2 text-sm text-zinc-300 shadow-md leading-relaxed"
                >
                  By choosing this Pre-Release Option, Customer (1) agrees that
                  Customer will not upload any User Content that includes
                  confidential information, controlled or sensitive data,
                  including personal data, protected health information, or
                  payment card industry information, or other information that is
                  regulated or requires enhanced security or special treatment;
                  and (2) understands that the Pre-Release Option has reduced
                  security standards relative to other NVIDIA Brev options.
                  <TooltipPrimitives.Arrow className="fill-zinc-800" />
                </TooltipPrimitives.Content>
              </TooltipPrimitives.Portal>
            </TooltipPrimitives.Root>
          </TooltipPrimitives.Provider>
        </span>
      </div>
    )}
    {selectedInstance.preemptible && (
      <div className="flex flex-row mb-1">
        <StopCircleIcon className="h-4 w-4" />
        <span className="ml-1 text-sm">
          This is a preemptible instance: a discounted but interruptible
          computing resource
        </span>
      </div>
    )}
    {stoppable ? (
      <div className="flex flex-row mb-1">
        <ArrowPathIcon className="h-4 w-4" />
        <span className="ml-1 text-sm">
          This instance can be stopped and restarted without losing data
        </span>
      </div>
    ) : (
      <div className="flex flex-row items-start mb-1">
        <NoSymbolIcon className="h-4 w-4 shrink-0 mt-0.5" />
        <span className="ml-1 text-sm">
          This instance cannot be stopped or restarted.
          <br />
          If your organization runs out of credits, Brev deletes the instance
          and all data on it. Deleted data cannot be recovered.
        </span>
      </div>
    )}
    {selectedInstance.estimated_deploy_time && (
      <div className="flex flex-row mb-1">
        <ClockIcon className="h-4 w-4" />
        <span className="ml-1 text-sm">
          This instance can take up to {selectedInstance.estimated_deploy_time}{" "}
          to deploy
        </span>
      </div>
    )}
  </div>
);

interface CostBreakdownProps {
  computePrice: number;
  storageCost: number;
  storagePricePerGbHrAmount: number;
  isFreeTier: boolean;
  isStorageCostFree: boolean;
  stoppable: boolean;
}

const CostBreakdown: React.FC<CostBreakdownProps> = ({
  computePrice,
  storageCost,
  storagePricePerGbHrAmount,
  isFreeTier,
  isStorageCostFree,
  stoppable,
}) => (
  <div>
    <PricingRateRows
      computePrice={computePrice}
      storageCost={storageCost}
      pricePerGbHr={storagePricePerGbHrAmount}
      isFreeTierCompute={isFreeTier}
      isFreeTierStorage={isStorageCostFree}
    />
    {stoppable &&
      (!storagePricePerGbHrAmount || isStorageCostFree ? (
        <p className="text-sm text-zinc-400 mt-3">
          You will not be charged while this instance is stopped.
        </p>
      ) : (
        <p className="text-sm font-normal text-white mt-3">
          While stopped:{" "}
          <span className="font-bold">${storageCost.toFixed(2)}/hr</span> for
          storage
        </p>
      ))}
  </div>
);

const SelectedInstanceDetails: React.FC<SelectedInstanceDetailsProps> = ({
  selectedInstance,
  setSelectedInstance,
  storage,
  setStorage,
  flexiblePortControlOnly = false,
  flexibleStorageOnly = false,
  locationName,
}) => {
  const { isEntitled } = useFreeTier();
  const basePriceAmount = getBasePriceAmount(selectedInstance, locationName);
  const isFreeTier = isEntitled && isFreeTierInstance(basePriceAmount);
  const isBrevcloud = isBrevCloud(selectedInstance.provider);
  const {
    pricePerGbHr: storagePricePerGbHrAmount,
    totalCost: storageCost,
    withinFreeTier: isStorageWithinFreeTier,
  } = getStoragePricing(selectedInstance, Number(storage));
  const isStorageCostFree = isEntitled && isStorageWithinFreeTier;
  const isCpu =
    !selectedInstance.supported_gpus ||
    selectedInstance.supported_gpus.length === 0;
  const stoppable = isStoppableInstance(selectedInstance?.stoppable);
  const computePrice = withServiceCharge(basePriceAmount);

  return (
    <>
      <div className={cx("w-full", isBrevcloud ? "mb-4" : "")}>
        {isCpu ? (
          <CpuInstanceCard
            instance={selectedInstance}
            selected
            onSelect={() => setSelectedInstance(undefined)}
            isFreeTier={isFreeTier}
            flexiblePortControlOnly={flexiblePortControlOnly}
            flexibleStorageOnly={flexibleStorageOnly}
            hidePrice
          />
        ) : (
          <div onClick={() => setSelectedInstance(undefined)}>
            <RackCard
              key={selectedInstance.type}
              instanceType={selectedInstance}
              setInstanceType={setSelectedInstance}
              gpuCount={selectedInstance.supported_gpus?.[0]?.count || 0}
              vramSize={selectedInstance.supported_gpus?.[0]?.memory || ""}
              ramSize={String(selectedInstance.memory)}
              cpuCount={selectedInstance.vcpu}
              cloudProvider={GetProviderName(selectedInstance) || "unknown"}
              cloud={GetCloudName(selectedInstance)}
              price=""
              isFreeTier={isFreeTier}
              selected={true}
            />
          </div>
        )}
      </div>

      {!isBrevcloud && (
        <div className="mt-5">
          {/* xl+: 3-column layout */}
          <div className="hidden xl:flex xl:gap-6 xl:divide-x xl:divide-zinc-800">
            <div className="flex-1">
              <div className="mb-2">
                <span className="text-sm font-medium text-primary">
                  Configure Storage
                </span>
                <p className="text-sm text-zinc-400 mt-0.5">
                  Storage is billed based on the amount provisioned and
                  continues to accrue even when your instance is stopped.
                </p>
              </div>
              <StorageSection
                selectedInstance={selectedInstance}
                storage={storage}
                setStorage={setStorage}
              />
            </div>
            <div className="flex-1 pl-6">
              <div className="flex items-center mb-2">
                <span className="text-sm font-medium text-primary">
                  Instance Attributes
                </span>
              </div>
              <InstanceAttributesList
                selectedInstance={selectedInstance}
                stoppable={stoppable}
              />
            </div>
            <div className="flex-1 pl-6">
              <div className="flex items-center mb-3">
                <span className="text-sm font-medium text-primary">
                  Cost Breakdown
                </span>
              </div>
              <CostBreakdown
                computePrice={computePrice}
                storageCost={storageCost}
                storagePricePerGbHrAmount={storagePricePerGbHrAmount}
                isFreeTier={isFreeTier}
                isStorageCostFree={isStorageCostFree}
                stoppable={stoppable}
              />
            </div>
          </div>

          {/* < xl: storage on top, then attributes + cost breakdown */}
          <div className="xl:hidden flex flex-col gap-6">
            <div>
              <div className="mb-2">
                <span className="text-sm font-medium text-primary">
                  Configure Storage
                </span>
                <p className="text-sm text-zinc-400 mt-0.5">
                  Storage is billed based on the amount provisioned and
                  continues to accrue even when your instance is stopped.
                </p>
              </div>
              <StorageSection
                selectedInstance={selectedInstance}
                storage={storage}
                setStorage={setStorage}
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:gap-6 sm:divide-x sm:divide-zinc-800 gap-6 border-t border-zinc-800 pt-6">
              <div className="sm:flex-1">
                <div className="flex items-center mb-2">
                  <span className="text-sm font-medium text-primary">
                    Instance Attributes
                  </span>
                </div>
                <InstanceAttributesList
                  selectedInstance={selectedInstance}
                  stoppable={stoppable}
                />
              </div>
              <div className="sm:flex-1 sm:pl-6">
                <div className="flex items-center mb-3">
                  <span className="text-sm font-medium text-primary">
                    Cost Breakdown
                  </span>
                </div>
                <CostBreakdown
                  computePrice={computePrice}
                  storageCost={storageCost}
                  storagePricePerGbHrAmount={storagePricePerGbHrAmount}
                  isFreeTier={isFreeTier}
                  isStorageCostFree={isStorageCostFree}
                  stoppable={stoppable}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SelectedInstanceDetails;
